import React, { useState } from "react";
import { TabPane, Row, Col } from "reactstrap";
import "./index.scss";
import ApiCaller from "../../../../api/apiCaller";
import CustomTextInput from "../../../../components/CustomTextInput";
import CustomDropdownInput from "../../../../components/CustomDropdownInput";
import CityPicker from "../../../../components/CityPicker";
import { Button } from "shared";
import Map from "pages/Map";
import t from "utils/Lang";
import { getLayoutSize, getLayoutType } from "utils/SiteHelper";

interface SiteInformationTabProps {
  isView: boolean;
  siteData: any;
  setSiteData: Function;
  storeFormatOptions: any;
  storeTypeOptions: any;
  parkingSiteTypeOptions: any;
  externalEnvironmentFactorOptions: any;
  siteSurroundingAreaTypeOptions: any;
  sitePositionTypeOptions: any;
  locationTypeOptions: any;
  allowEditLocationAddress: boolean;
  invalidFields?: any;
  siteTypeOptions: Array<any>;
  groups: Array<any>;
}

export default function SiteInformationTab({
  isView,
  siteData,
  setSiteData,
  storeFormatOptions,
  storeTypeOptions,
  parkingSiteTypeOptions,
  externalEnvironmentFactorOptions,
  siteSurroundingAreaTypeOptions,
  sitePositionTypeOptions,
  locationTypeOptions,
  allowEditLocationAddress,
  invalidFields,
  siteTypeOptions,
  groups,
}: SiteInformationTabProps) {
  const [isShowMap, setIsShowMap] = useState(false);

  const onConfirmPinLocation = (point: { lat?: number; lng?: number }) => {
    setSiteData({
      ...siteData,
      data: {
        ...siteData.data,
        latitude: point.lat,
        longitude: point.lng,
      },
    });
  };

  const getSiteLatLong = async () => {
    const r = window.confirm(t().site_information_tab__confirm_auto_geo);
    if (!r) return;
    const addressToLookup = `${siteData.data.address_number} ${
      siteData.data.street_name
    }, ${siteData.data.ward?.name.replace("Phường", "")}, ${
      siteData.data.district?.name
    }, ${siteData.data.city?.name.replace("Thành phố", "")}`;
    let uri = `location/geocoding/address_to_lat_lng?address=${addressToLookup}`;
    if (siteData.data.plus_code) {
      uri = `${uri}&plus_code=${encodeURI(siteData.data.plus_code)}`;
    }
    const res = await ApiCaller.get(uri);
    if (res?.result) {
      setSiteData({
        ...siteData,
        data: {
          ...siteData.data,
          latitude: res.result.latitude,
          longitude: res.result.longitude,
        },
      });
    } else {
      setSiteData({ ...siteData, ...{ loading: false } });
    }
  };

  const updateAttr = (attribute: string, value: any) => {
    let dataUpdate = {};
    if (typeof value === "object") {
      if (value?.label) {
        // Check value is object (value from dropdown menu)
        dataUpdate = {
          [attribute]: {
            id: value?.value,
            name: value?.label,
          },
        };
      } else {
        dataUpdate = {
          [attribute]: null,
        };
      }
    } else {
      dataUpdate = { [attribute]: value };
    }
    setSiteData({
      ...siteData,
      ...{ data: { ...siteData?.data, ...dataUpdate } },
    });
  };

  const updateSiteCity = (selectedCity: any) => {
    setSiteData({
      ...siteData,
      ...{ data: { ...siteData?.data, ...selectedCity } },
    });
  };

  const toggleMap = () => {
    setIsShowMap(!isShowMap);
  };

  const isSiteInBuilding = siteData?.data?.location?.location_type?.name
    ?.toLowerCase()
    .includes("building");

  const conditionalShowMap = () => {
    return true;
    // return siteData?.data &&
    // siteData?.data?.latitude &&
    // siteData?.data?.longitude &&
    // Number.isFinite(siteData?.data?.latitude) &&
    // siteData?.data?.latitude >= -90 &&
    // siteData?.data?.latitude <= 90 &&
    // Number.isFinite(siteData?.data?.longitude) &&
    // siteData?.data?.longitude >= -180 &&
    // siteData?.data?.longitude <= 180
  };

  let uiLayoutDefinition: any = [];

  const optionMapping: any = {
    store_format: storeFormatOptions,
    store_type: storeTypeOptions,
    location_type: locationTypeOptions,
    store_site_type: siteTypeOptions,
    site_position_type: sitePositionTypeOptions,
    primary_site_surrounding_area_type: siteSurroundingAreaTypeOptions,
    secondary_site_surrounding_area_type: siteSurroundingAreaTypeOptions,
    external_environmental_factor: externalEnvironmentFactorOptions,
    primary_motorbike_parking_site_type: parkingSiteTypeOptions,
    secondary_motorbike_parking_site_type: parkingSiteTypeOptions,
    car_parking_site_type: parkingSiteTypeOptions,
  };
  uiLayoutDefinition = groups
    .filter((g) => {
      if (!isSiteInBuilding) return g.code !== "site_information_building";
      return true;
    })
    .map((g) => {
      const items = g.fields.map((f: any) => {
        return {
          attributeName: f.code,
          type: getLayoutType(f.field_data_type),
          size: getLayoutSize(f.code),
          labelText: f.name,
          isNumeric:
            f.field_data_type === "number" || f.field_data_type === "decimal",
          isDecimal: f.field_data_type === "decimal",
          options:
            f.field_data_type === "dropdown"
              ? optionMapping?.[f.code] || []
              : undefined,
        };
      });
      if (g.code === "site_information_address") {
        items.splice(1, 0, {
          type: "custom",
          size: 12,
          component: (
            <CityPicker
              isView={!allowEditLocationAddress}
              onSelect={(selectedData) => updateSiteCity(selectedData)}
              city={siteData.data.city}
              district={siteData.data.district}
              ward={siteData.data.ward}
              elementToShow={["city", "district", "ward"]}
            />
          ),
        });
        items.push(
          {
            type: "custom",
            size: 6,
            component: (
              <div>
                {allowEditLocationAddress && (
                  <Button
                    label={t().site_information_tab__auto_lat_lng}
                    type="primary"
                    outline
                    onClick={getSiteLatLong}
                    style={styles.controlButton}
                    size="small"
                  />
                )}
              </div>
            ),
          },
          {
            type: "custom",
            size: 6,
            component: (
              <Button
                label={
                  isShowMap
                    ? t().site_information_tab__hide_map
                    : t().site_information_tab__show_map
                }
                type="primary"
                outline
                onClick={toggleMap}
                style={styles.controlButton}
                size="small"
              />
            ),
          },
          {
            type: "custom",
            size: 12,
            component: !isShowMap ? null : (
              <div style={styles.mapDiv}>
                {isShowMap && conditionalShowMap() && (
                  <Map
                    latitude={parseFloat(siteData?.data?.latitude)}
                    longitude={parseFloat(siteData?.data?.longitude)}
                    embedMode
                    pinMode
                    onConfirmPinLocation={onConfirmPinLocation}
                  />
                )}
              </div>
            ),
          }
        );
      }
      return {
        type: "row",
        label: g.name,
        items,
      };
    });

  return (
    <TabPane tabId="store_information_tab" style={{}}>
      {uiLayoutDefinition.map((row: any, index: number) => (
        <div key={index}>
          <h4 className="site-summary__label-title">{row.label}</h4>
          <Row className="site-information-tab__section">
            {row.items.map((layoutItem: any, layoutIndex: number) => {
              if (layoutItem.type === "dropdown") {
                return (
                  <Col
                    key={`layout-${index}-${layoutIndex}`}
                    sm={{ size: layoutItem.size, offset: 0 }}
                  >
                    <CustomDropdownInput
                      disabled={layoutItem.isView || isView}
                      style={styles.customTextInput}
                      callback={updateAttr}
                      data={siteData?.data}
                      options={layoutItem.options}
                      labels={layoutItem?.labelText ? null : siteData?.labels}
                      labelText={layoutItem?.labelText}
                      attributeName={layoutItem.attributeName}
                    />
                  </Col>
                );
              } else if (
                layoutItem.type === "text" ||
                layoutItem.type === "textarea" ||
                layoutItem.type === "date"
              ) {
                return (
                  <Col
                    key={`layout-index-${layoutIndex}`}
                    sm={{ size: layoutItem.size, offset: 0 }}
                  >
                    <CustomTextInput
                      type={layoutItem.type}
                      isNumeric={layoutItem.isNumeric || false}
                      isDecimal={layoutItem.isDecimal || false}
                      disabled={layoutItem.isView || isView}
                      style={styles.customTextInput}
                      callback={updateAttr}
                      data={siteData?.data}
                      labels={layoutItem?.labelText ? null : siteData?.labels}
                      labelText={layoutItem?.labelText}
                      attributeName={layoutItem.attributeName}
                      invalid={
                        invalidFields[layoutItem.attributeName] ? true : false
                      }
                      formFeedbackText={
                        invalidFields[layoutItem.attributeName]
                          ? `${
                              siteData.labels[layoutItem.attributeName]
                            } không được để trống`
                          : ""
                      }
                      inputBoxRows={layoutItem.inputBoxRows}
                    />
                  </Col>
                );
              } else if (layoutItem.type === "break") {
                return (
                  <Col
                    key={`layout-index-${layoutIndex}`}
                    sm={{ size: 6, offset: 0 }}
                  />
                );
              } else if (layoutItem.type === "custom") {
                return (
                  <Col
                    key={`layout-index-${layoutIndex}`}
                    sm={{ size: layoutItem.size, offset: 0 }}
                  >
                    {layoutItem.component}
                  </Col>
                );
              }
            })}
          </Row>
        </div>
      ))}
    </TabPane>
  );
}

const styles = {
  customTextInput: {
    marginTop: 20,
  },
  controlButton: {
    marginTop: 12,
    marginBottom: 12,
  },
  mapDiv: {
    marginTop: 12,
    marginBottom: 12,
  },
};
